import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { PaperAirplaneIcon, SparklesIcon, TrashIcon, CheckIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import {
  IChatMessage,
  ChatContextType,
  ChatMessageRole,
  LLMStreamEventType,
  ScriptActionCard,
  ScriptActionType,
  ScriptActionTypeLabels,
  ScriptAIResponse,
  getLabelsByLocale,
  Locale
} from '@packages/global';
import { chatMessageApi, llmApi } from '../../services/user';
import { useToast } from '../../stores/toastStore';
import { useTranslation } from 'react-i18next';

export interface ScriptChatPanelRef {
  triggerGenerate: (prompt: string) => void;
}

interface ScriptChatPanelProps {
  projectId: string;
  chapterId: string;
  chapterTitle: string;
  onApplyContent: (content: string, title?: string) => void;
}

const ScriptChatPanel = forwardRef<ScriptChatPanelRef, ScriptChatPanelProps>(({
  projectId, chapterId, chapterTitle, onApplyContent
}, ref) => {
  const toast = useToast();
  const { t, i18n } = useTranslation(['script', 'common']);
  const scriptActionTypeLabels = getLabelsByLocale(ScriptActionTypeLabels, i18n.language as Locale);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    loadMessages();
    return () => { abortControllerRef.current?.abort(); };
  }, [projectId, chapterId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, streamingContent]);

  useImperativeHandle(ref, () => ({
    triggerGenerate: (prompt: string) => { handleSendWithPrompt(prompt); }
  }));

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatMessageApi.list({ projectId, contextType: ChatContextType.SCRIPT, contextId: chapterId, limit: 50 });
      setMessages(data);
    } catch (error) {
      console.error('加载消息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendWithPrompt = async (prompt: string) => {
    if (streaming) return;
    setStreaming(true);
    setStreamingContent('');
    const tempUserMsg: IChatMessage = {
      id: `temp-${Date.now()}`, projectId, contextType: ChatContextType.SCRIPT, contextId: chapterId,
      role: ChatMessageRole.USER, content: prompt, createdAt: new Date(), updatedAt: new Date()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    try {
      abortControllerRef.current = new AbortController();
      await llmApi.stream(
        { projectId, contextType: ChatContextType.SCRIPT, contextId: chapterId, prompt },
        (event) => {
          switch (event.type) {
            case LLMStreamEventType.DELTA:
              setStreamingContent(prev => prev + (event.content || ''));
              break;
            case LLMStreamEventType.DONE:
              const fullContent = event.content || '';
              const aiMsg: IChatMessage = {
                id: `ai-${Date.now()}`, projectId, contextType: ChatContextType.SCRIPT, contextId: chapterId,
                role: ChatMessageRole.ASSISTANT, content: fullContent, appliedActionIndexes: [],
                createdAt: new Date(), updatedAt: new Date()
              };
              setMessages(prev => [...prev, aiMsg]);
              setStreamingContent('');
              setStreaming(false);
              break;
            case LLMStreamEventType.ERROR:
              toast.error(event.error || t('chatPanel.aiFailed'));
              setStreaming(false);
              setStreamingContent('');
              break;
          }
        },
        abortControllerRef.current.signal
      );
    } catch (error: any) {
      if (error.name !== 'AbortError') { toast.error(t('chatPanel.sendFailed')); }
      setStreaming(false);
      setStreamingContent('');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const msg = input.trim();
    setInput('');
    await handleSendWithPrompt(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleClear = async () => {
    if (!confirm(t('chatPanel.clearConfirm'))) return;
    try {
      await chatMessageApi.clear(projectId, ChatContextType.SCRIPT, chapterId);
      setMessages([]);
      toast.success(t('chatPanel.cleared'));
    } catch { toast.error(t('chatPanel.clearFailed')); }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (messageId.startsWith('temp-') || messageId.startsWith('ai-')) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      return;
    }
    try {
      await chatMessageApi.delete(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch { toast.error(t('chatPanel.deleteFailed')); }
  };

  const handleApplyAction = async (action: ScriptActionCard, messageId: string, actionIndex: number) => {
    try {
      onApplyContent(action.content, action.title);
      toast.success(t('chatPanel.applied'));
      if (!messageId.startsWith('ai-')) {
        await chatMessageApi.markActionApplied(messageId, actionIndex);
      }
      setMessages(prev => prev.map(m => {
        if (m.id === messageId) {
          return { ...m, appliedActionIndexes: [...(m.appliedActionIndexes || []), actionIndex] };
        }
        return m;
      }));
    } catch { toast.error(t('chatPanel.applyFailed')); }
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const parseMessageContent = (content: string): { text: string; actions: ScriptActionCard[] } => {
    try {
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) { const p = JSON.parse(jsonMatch[1]) as ScriptAIResponse; return { text: p.message, actions: p.actions || [] }; }
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) { const p = JSON.parse(trimmed) as ScriptAIResponse; return { text: p.message, actions: p.actions || [] }; }
      const m = content.match(/\{[\s\S]*"message"[\s\S]*\}/);
      if (m) { const p = JSON.parse(m[0]) as ScriptAIResponse; return { text: p.message, actions: p.actions || [] }; }
      return { text: content, actions: [] };
    } catch { return { text: content, actions: [] }; }
  };

  return (
    <div className="h-full flex flex-col bg-bg-page rounded-xl border border-border-subtle overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-50">{t('chatPanel.title')}</span>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button onClick={handleClear} className="p-1.5 text-gray-400 hover:text-error transition-colors cursor-pointer" title={t('chatPanel.clearChat')}>
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-success/10 rounded">
            <div className="w-1.5 h-1.5 bg-success rounded-full" />
            <span className="text-[11px] text-success font-medium">{t('chatPanel.online')}</span>
          </div>
        </div>
      </div>

      {/* Chapter Info */}
      <div className="px-4 py-2 border-b border-border-subtle bg-bg-surface flex-shrink-0">
        <div className="flex items-center gap-2 text-[12px] text-gray-400">
          <DocumentTextIcon className="w-3.5 h-3.5" />
          <span>{t('chatPanel.currentChapter')}</span>
          <span className="text-gray-200">{chapterTitle}</span>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400">
            <span className="text-sm">{t('common:loading')}</span>
          </div>
        ) : (
          <>
            <div className="flex gap-2.5">
              <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                <p className="text-[13px] text-gray-200 leading-relaxed">
                  {t('chatPanel.welcomeMessage')}
                </p>
              </div>
            </div>

            {messages.map((msg) => {
              const isUser = msg.role === ChatMessageRole.USER;
              const { text, actions } = isUser ? { text: msg.content, actions: [] } : parseMessageContent(msg.content);
              const appliedIndexes = msg.appliedActionIndexes || [];

              return (
                <div key={msg.id} className="flex flex-col gap-2">
                  <div className={`group flex gap-2.5 ${isUser ? 'justify-end' : ''}`}>
                    {!isUser && (
                      <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`flex flex-col gap-1 max-w-[85%] ${isUser ? 'items-end' : ''}`}>
                      <div className={`rounded-xl px-3 py-2.5 ${isUser ? 'bg-primary/10 border border-primary' : 'bg-bg-elevated'}`}>
                        <p className={`text-[13px] leading-relaxed whitespace-pre-wrap break-words ${isUser ? 'text-primary' : 'text-gray-200'}`}>
                          {text.length > 300 ? text.slice(0, 300) + '...' : text}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
                        {!isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-error transition-colors cursor-pointer" title={t('chatPanel.deleteMessage')}>
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                        <span className={`text-[11px] ${isUser ? 'text-primary/50' : 'text-gray-400'}`}>{formatTime(msg.createdAt)}</span>
                        {isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-error transition-colors cursor-pointer" title={t('chatPanel.deleteMessage')}>
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {!isUser && actions.length > 0 && (
                    <div className="flex flex-col gap-2 ml-9">
                      {actions.map((action, idx) => {
                        const isApplied = appliedIndexes.includes(idx);
                        return (
                          <div key={idx} className={`rounded-lg border p-3 ${isApplied ? 'bg-bg-page border-border-subtle' : 'bg-bg-surface border-border-default'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-[11px] font-semibold text-white rounded bg-success ${isApplied ? 'opacity-50' : ''}`}>
                                  {scriptActionTypeLabels[action.actionType]}
                                </span>
                                <span className={`text-[13px] font-semibold ${isApplied ? 'text-gray-400' : 'text-gray-50'}`}>
                                  {action.title || chapterTitle}
                                </span>
                              </div>
                              {isApplied ? (
                                <span className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-500">
                                  <CheckIcon className="w-3 h-3" />{t('chatPanel.alreadyApplied')}
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleApplyAction(action, msg.id, idx)}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-success text-success transition-colors hover:bg-success/10 cursor-pointer"
                                >
                                  <CheckIcon className="w-3 h-3" />{t('chatPanel.apply')}
                                </button>
                              )}
                            </div>
                            {action.summary && (
                              <p className={`text-[12px] leading-relaxed line-clamp-2 mb-2 ${isApplied ? 'text-gray-500' : 'text-gray-300'}`}>
                                {action.summary}
                              </p>
                            )}
                            <div className={`text-[12px] leading-relaxed max-h-[200px] overflow-y-auto ${isApplied ? 'text-gray-500' : 'text-gray-400'}`}>
                              <p className="whitespace-pre-wrap">{action.content}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {streaming && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                  {streamingContent ? (
                    <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">{streamingContent}</p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-[13px] text-gray-400">{t('chatPanel.thinking')}</span>
                    </div>
                  )}
                  {streamingContent && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                      <span className="text-[11px] text-gray-400">{t('chatPanel.generating')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex items-end gap-3 p-4 border-t border-border-subtle flex-shrink-0">
        <div className="flex-1">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatPanel.inputPlaceholder')}
            className="w-full bg-bg-elevated text-gray-50 text-[13px] rounded-lg px-3.5 py-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-gray-400"
            rows={1}
            disabled={streaming}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || streaming}
          className="w-10 h-10 bg-primary hover:bg-primary/90 disabled:bg-bg-elevated disabled:text-gray-400 text-white rounded-lg flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer disabled:cursor-not-allowed"
        >
          <PaperAirplaneIcon className="w-[18px] h-[18px]" />
        </button>
      </div>
    </div>
  );
});

ScriptChatPanel.displayName = 'ScriptChatPanel';
export default ScriptChatPanel;
